import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { landingsApi, launchesApi } from "../../api/endpoints";
import { ApiError } from "../../api/client";
import { launchAvatarText } from "../../lib/launchAvatar";
import type { Landing, LandingStatus } from "../../types/domain";
import { LANDING_STATUS_META } from "./landingStatus";
import { IcoChevLeft, IcoTrash } from "../../components/icons";
import { MdEditor } from "../../components/MdEditor";
import { mdToHtml } from "../../lib/md";
import { useCanWrite } from "../../hooks/useCanWrite";
import { ConflictDialog, type ConflictFieldDiff } from "../../components/ConflictDialog";
import { ConfirmDialog } from "../../components/ConfirmDialog";

const STATUS_META = LANDING_STATUS_META;

export function LandingEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const readOnly = !canWrite;

  const landingQuery = useQuery({
    queryKey: ["landing", id],
    queryFn: async () => (await landingsApi.get(id!)).landing,
    enabled: !!id,
    staleTime: Infinity, // Local state owns the editor; don't refetch and trample the cursor.
  });
  const launchesQuery = useQuery({
    queryKey: ["launches"],
    queryFn: async () => (await launchesApi.list()).launches,
  });

  const [initialized, setInitialized] = useState(false);
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState<LandingStatus>("draft");
  const [url, setUrl] = useState("");
  const [launchId, setLaunchId] = useState("");
  const [mdSource, setMdSource] = useState("");
  const [dirty, setDirty] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [baselineUpdatedAt, setBaselineUpdatedAt] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Landing | null>(null);
  const [askDelete, setAskDelete] = useState(false);

  // One-shot initialization from the loaded landing.
  useEffect(() => {
    if (initialized || !landingQuery.data) return;
    const l = landingQuery.data;
    setTitle(l.title ?? "");
    setStatus(l.status);
    setUrl(l.url ?? "");
    setLaunchId(l.launchId ?? "");
    setMdSource(l.content ?? "");
    setBaselineUpdatedAt(l.updatedAt);
    setInitialized(true);
  }, [landingQuery.data, initialized]);

  const patchMutation = useMutation({
    mutationFn: (vars: { patch: Partial<Landing>; force?: boolean }) =>
      landingsApi.patch(
        id!,
        vars.patch,
        vars.force || !baselineUpdatedAt
          ? undefined
          : { expectedUpdatedAt: baselineUpdatedAt },
      ),
    onSuccess: ({ landing }) => {
      queryClient.setQueryData(["landing", id], landing);
      queryClient.invalidateQueries({ queryKey: ["landings"] });
      setBaselineUpdatedAt(landing.updatedAt);
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 409) {
        const current = (err.details as { current?: Landing } | undefined)?.current;
        if (current) setConflict(current);
      }
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => landingsApi.remove(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landings"] });
      navigate("/landings");
    },
  });

  // Debounced autosave — resets on every dep change until the user pauses.
  // While a conflict dialog is open, pause autosave until the user resolves it.
  useEffect(() => {
    if (!dirty || readOnly || conflict) return;
    const t = setTimeout(() => {
      patchMutation.mutate({
        patch: {
          title,
          status,
          url: url || null,
          launchId: launchId || null,
          content: mdSource,
        },
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      setDirty(false);
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, title, status, url, launchId, mdSource, readOnly, conflict]);

  const handleConflictKeepMine = () => {
    patchMutation.mutate({
      patch: {
        title,
        status,
        url: url || null,
        launchId: launchId || null,
        content: mdSource,
      },
      force: true,
    });
    setConflict(null);
  };
  const handleConflictTakeTheirs = () => {
    if (!conflict) return;
    setTitle(conflict.title);
    setStatus(conflict.status);
    setUrl(conflict.url ?? "");
    setLaunchId(conflict.launchId ?? "");
    setMdSource(conflict.content);
    setBaselineUpdatedAt(conflict.updatedAt);
    setDirty(false);
    setConflict(null);
  };

  function buildLandingDiff(server: Landing): ConflictFieldDiff[] {
    const out: ConflictFieldDiff[] = [];
    if (title !== server.title) out.push({ label: "Заголовок", yours: title, theirs: server.title });
    if (status !== server.status)
      out.push({ label: "Статус", yours: status, theirs: server.status });
    if ((url || null) !== (server.url ?? null))
      out.push({ label: "URL", yours: url || "—", theirs: server.url || "—" });
    if ((launchId || null) !== (server.launchId ?? null))
      out.push({
        label: "Запуск",
        yours: launchId || "—",
        theirs: server.launchId ?? "—",
      });
    if (mdSource !== server.content)
      out.push({ label: "Содержимое", yours: mdSource, theirs: server.content });
    return out;
  }

  const markDirty = () => {
    if (readOnly) return;
    setDirty(true);
  };

  const handleDelete = () => {
    setAskDelete(true);
  };

  if (!id) {
    navigate("/landings");
    return null;
  }

  if (landingQuery.isLoading || !initialized) {
    return (
      <div className="launches-empty">
        <p>Загружаем лендинг…</p>
      </div>
    );
  }

  if (landingQuery.isError || !landingQuery.data) {
    return (
      <div className="launches-empty">
        <h3>Лендинг не найден</h3>
        <p>
          Возможно, он удалён.{" "}
          <button
            type="button"
            className="btn-ghost"
            onClick={() => navigate("/landings")}
          >
            Вернуться к списку
          </button>
        </p>
      </div>
    );
  }

  const launches = launchesQuery.data ?? [];

  return (
    <div className="landing-editor">
      <div className="landing-editor-topbar">
        <button
          type="button"
          className="topbar-back-btn"
          onClick={() => navigate("/landings")}
          title="К списку лендингов"
        >
          <IcoChevLeft size={14} />
          <span>Лендинги</span>
        </button>
        <div className="landing-editor-crumbs">
          <span className="crumb-sep">/</span>
          <span className="crumb-current">{title || "Без названия"}</span>
        </div>

        <div className="landing-editor-save-state">
          {readOnly ? (
            <span className="landing-editor-clean">Только просмотр</span>
          ) : dirty ? (
            <span className="landing-editor-dirty">● Несохранено</span>
          ) : savedFlash ? (
            <span className="landing-editor-saved">✓ Сохранено</span>
          ) : (
            <span className="landing-editor-clean">Все изменения сохранены</span>
          )}
        </div>
      </div>

      <div className="landing-editor-meta">
        <input
          className="landing-editor-title-input"
          placeholder="Название лендинга…"
          value={title}
          disabled={readOnly}
          onChange={(e) => {
            setTitle(e.target.value);
            markDirty();
          }}
        />
        <div className="landing-editor-meta-row">
          <div className="landing-meta-field">
            <label>Статус</label>
            <div className="landing-status-select">
              {(Object.keys(STATUS_META) as LandingStatus[]).map((k) => {
                const v = STATUS_META[k];
                return (
                  <button
                    key={k}
                    type="button"
                    className={"landing-status-opt" + (status === k ? " active" : "")}
                    style={status === k ? { background: v.bg, color: v.color } : {}}
                    disabled={readOnly}
                    onClick={() => {
                      if (readOnly) return;
                      setStatus(k);
                      markDirty();
                    }}
                  >
                    <span className="status-dot" style={{ background: v.dotColor }} />
                    {v.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="landing-meta-field">
            <label>Запуск</label>
            <select
              className="form-input mini"
              value={launchId}
              disabled={readOnly}
              onChange={(e) => {
                setLaunchId(e.target.value);
                markDirty();
              }}
            >
              <option value="">Без запуска</option>
              {launches.map((l) => (
                <option key={l.id} value={l.id}>
                  {launchAvatarText(l)} {l.name}
                </option>
              ))}
            </select>
          </div>
          <div className="landing-meta-field landing-meta-url">
            <label>URL (если опубликован)</label>
            <input
              className="form-input mini"
              placeholder="https://…"
              value={url}
              disabled={readOnly}
              onChange={(e) => {
                setUrl(e.target.value);
                markDirty();
              }}
            />
          </div>
        </div>
      </div>

      {readOnly ? (
        <div
          className="md-readonly-preview landing-readonly-body"
          dangerouslySetInnerHTML={{ __html: mdToHtml(mdSource || "") as string }}
        />
      ) : (
        <MdEditor
          value={mdSource}
          onChange={(md) => {
            setMdSource(md);
            markDirty();
          }}
          showImageButton
          minHeight="100%"
          toolbarExtra={
            <button type="button" className="lt-btn lt-danger" onClick={handleDelete}>
              <IcoTrash size={13} /> Удалить
            </button>
          }
        />
      )}
      {conflict && (
        <ConflictDialog
          title="Лендинг был изменён"
          updatedAt={conflict.updatedAt}
          fields={buildLandingDiff(conflict)}
          onKeepMine={handleConflictKeepMine}
          onTakeTheirs={handleConflictTakeTheirs}
          onClose={() => setConflict(null)}
          busy={patchMutation.isPending}
        />
      )}
      {askDelete && (
        <ConfirmDialog
          title="Удалить лендинг?"
          message={`«${title || "Без названия"}» будет удалён без возможности восстановления.`}
          confirmLabel="Удалить лендинг"
          busy={removeMutation.isPending}
          onConfirm={() => removeMutation.mutate()}
          onClose={() => setAskDelete(false)}
        />
      )}
    </div>
  );
}
